import {
  AccountActive,
  AccountArchived,
  AccountBusy,
  AccountNotFound,
  ClientOrderIdConflict,
  IllegalRunnerCommand,
  InvalidAccountConfig,
  OrderDenied,
  OrderNotCancellable,
  RunnerNotFound
} from "@trader/shared/Errors"
import type { DenyReason } from "@trader/shared/Money"
import { Schema } from "effect"

// What the user reads when a trading request fails: a short title and a
// sentence that says what to do. Pure, so the wording is unit tested.

/** A headline per risk denial; the server's message adds the numbers. */
export const DENY_TITLES: Readonly<Record<DenyReason, string>> = {
  UnknownInstrument: "Unknown instrument",
  NotTradeable: "Not tradeable",
  CurrencyMismatch: "Wrong account currency",
  InvalidQty: "Invalid quantity",
  InvalidLotSize: "Not a multiple of the lot size",
  OffTickGrid: "Price not on the tick grid",
  TypeFields: "Check the order prices",
  ProductNotAllowed: "Product not allowed",
  ShortNotAllowed: "Cash can't sell short",
  NoReferencePrice: "No price yet",
  InsufficientBuyingPower: "Insufficient buying power",
  PositionOwnedByRunner: "A strategy owns this position",
  AccountHalted: "Account halted"
}

export interface ErrorMessage {
  readonly title: string
  readonly detail: string
}

const isDenied = Schema.is(OrderDenied)
const isConflict = Schema.is(ClientOrderIdConflict)
const isNotCancellable = Schema.is(OrderNotCancellable)
const isInvalidConfig = Schema.is(InvalidAccountConfig)
const isIllegalRunnerCommand = Schema.is(IllegalRunnerCommand)
const isArchived = Schema.is(AccountArchived)
const isActive = Schema.is(AccountActive)
const isNotFound = Schema.is(AccountNotFound)
const isBusy = Schema.is(AccountBusy)
const isRunnerNotFound = Schema.is(RunnerNotFound)

/**
 * The message for a failed request, from the server's error tag and its decoded
 * error (ApiError's `tag` and `detail`).
 */
export const describeError = (tag: string, detail: unknown): ErrorMessage => {
  if (isDenied(detail)) {
    const reason = detail.reason
    if (reason === "ShortNotAllowed" && /at most 0 /.test(detail.message)) {
      return { title: "No holdings to sell", detail: detail.message }
    }
    return { title: DENY_TITLES[reason], detail: detail.message }
  }
  if (isConflict(detail)) {
    return {
      title: "Already placed",
      detail: `This ticket already placed ${detail.orderId} with different details. Check the Orders tab before placing it again.`
    }
  }
  if (isNotCancellable(detail)) return { title: "Can't cancel", detail: `The order is already ${detail.status}.` }
  if (isInvalidConfig(detail)) return { title: "Can't create the account", detail: detail.message }
  if (isIllegalRunnerCommand(detail)) {
    return { title: "Not possible now", detail: `Can't ${detail.command} a strategy that is ${detail.status}.` }
  }
  if (isArchived(detail)) return { title: "Account is read-only", detail: "This account was reset or its session ended. Pick another account." }
  if (isActive(detail)) return { title: "Session still running", detail: "End the session first, then delete it." }
  if (isNotFound(detail)) return { title: "Account not found", detail: "This account no longer exists." }
  if (isBusy(detail)) return { title: "Account busy", detail: "The account is processing a step. Try again in a moment." }
  if (isRunnerNotFound(detail)) return { title: "Strategy not found", detail: "This strategy is no longer attached." }
  switch (tag) {
    case "ReplayFinished":
      return { title: "Replay finished", detail: "The replay reached the end of the stored bars." }
    case "NotReplayAccount":
      return { title: "Not a replay account", detail: "Only replay accounts can be played, paused or stepped." }
    case "OrderNotFound":
      return { title: "Order not found", detail: "The order no longer exists." }
    case "Unauthorized":
      return { title: "Signed out", detail: "Your session ended. Sign in again." }
    case "UnknownStrategy":
      return { title: "Unknown strategy", detail: "That strategy isn't available." }
    case "RunnerConflict":
      return { title: "Already running", detail: "A strategy already trades this instrument and product on this account." }
    case "ProductDisallowsShort":
      return { title: "Shorting not allowed", detail: "The strategy goes short, which this product or account doesn't allow." }
    case "InvalidRunnerParams":
      return { title: "Invalid strategy settings", detail: "Check the strategy's inputs and properties." }
    case "NonDeterministic":
      return { title: "Strategy not repeatable", detail: "Two runs of the strategy disagreed, so it can't be paper traded." }
    case "RequestError":
    case "HttpClientError":
    case "TransportError":
      return { title: "Server unreachable", detail: "Couldn't reach the server. Check the connection and try again." }
    default:
      return { title: "Something went wrong", detail: `The server couldn't do that (${tag}). Try again.` }
  }
}

/**
 * Whether a failed order request needs a new client order id. After any 4xx the
 * server has settled the id's fate (denied orders don't reserve it, conflicts
 * own it), so the next try is a new order. A 503 (busy) or a lost response may
 * have placed it: keep the id, so a retry is answered with the existing order.
 */
export const needsNewClientOrderId = (tag: string): boolean =>
  ["OrderDenied", "ClientOrderIdConflict", "AccountArchived", "AccountNotFound", "HttpApiDecodeError", "SchemaError", "BadRequest"].includes(
    tag
  )
